import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, filter, finalize, first, of, pairwise, Subject, takeUntil, tap } from 'rxjs';

import {
  NewVehicle,
  VehicleFacade,
  VehicleFeaturesAdapterService,
  VehicleFeaturesModel,
  VehicleService
} from '@amfam/policy/vehicle/data-access';
import { ValidationService } from '@amfam/ui-kit';

@Component({
  selector: 'amfam-msrp',
  templateUrl: './msrp.component.html',
  styleUrl: './msrp.component.scss',
  providers: [CurrencyPipe],
  standalone: false
})
export class MsrpComponent implements OnInit, OnDestroy {
  @Input() parentForm: FormGroup;
  @Output() vehicleDetailsLoaded = new EventEmitter<{
    newVehicle: NewVehicle;
    features: VehicleFeaturesModel[];
  }>();

  year: string;
  makes: string[];
  models: string[];
  series: string[];
  showMsrp = false;
  msrp: string;
  vehicleDetails: { newVehicle: NewVehicle; features: VehicleFeaturesModel[] };

  private stop$ = new Subject<void>();

  constructor(
    private vehicleFacade: VehicleFacade,
    private vehicleService: VehicleService,
    private cdr: ChangeDetectorRef,
    private vehicleFeaturesAdapterService: VehicleFeaturesAdapterService,
    private currencyPipe: CurrencyPipe
  ) {}

  public get vinControl(): AbstractControl {
    return this.parentForm.get('vin');
  }

  public get yearControl(): AbstractControl {
    return this.parentForm.get('year');
  }

  public get makeControl(): AbstractControl {
    return this.parentForm.get('make');
  }

  public get modelControl(): AbstractControl {
    return this.parentForm.get('model');
  }

  public get seriesControl(): AbstractControl {
    return this.parentForm.get('series');
  }

  public get msrpControl(): AbstractControl {
    return this.parentForm.get('msrp');
  }

  ngOnDestroy(): void {
    this.resetAll();
    this.stop$.next();
    this.stop$.complete();
  }

  ngOnInit(): void {
    this.vehicleFacade.makes$
      .pipe(
        takeUntil(this.stop$),
        tap(makes => (this.makes = makes))
      )
      .subscribe();

    this.vehicleFacade.models$
      .pipe(
        takeUntil(this.stop$),
        tap(models => (this.models = models))
      )
      .subscribe();

    this.vehicleFacade.series$
      .pipe(
        takeUntil(this.stop$),
        tap(series => (this.series = series))
      )
      .subscribe();

    this.parentForm.addControl('make', new FormControl('', Validators.required));
    this.parentForm.addControl('model', new FormControl('', Validators.required));
    this.parentForm.controls['model'].disable();
    this.loadMakes();

    this.parentForm.valueChanges
      .pipe(
        takeUntil(this.stop$),
        tap(value => {
          if ([value.make, value.model].includes('other')) {
            this.vehicleDetails = { newVehicle: null, features: null };
            this.vehicleDetailsLoaded.emit(this.vehicleDetails);
          }
        })
      )
      .subscribe();

    this.parentForm.valueChanges
      .pipe(
        takeUntil(this.stop$),
        pairwise(),
        filter(
          ([firstValue, second]) =>
            firstValue?.vin !== second?.vin || firstValue?.year !== second?.year
        ),
        tap(() => {
          this.clearAll();
        })
      )
      .subscribe();
  }

  loadMakes() {
    this.resetVehicleDetails();
    const { year } = this.parentForm.getRawValue();
    if (year && year !== 'unset') {
      const currentYear = new Date().getFullYear();
      if (year >= currentYear) this.year = String(currentYear);
      this.vehicleFacade.loadMakes(this.year);
    }
  }

  loadModels() {
    this.resetVehicleDetails();
    this.resetMsrp();
    const { make } = this.parentForm.getRawValue();
    this.parentForm.patchValue({ model: null, series: null });
    this.models = [];
    this.modelControl?.reset();
    this.series = [];
    this.seriesControl?.reset();

    if (make && make === 'other') {
      this.modelControl?.setValue('other');
      this.seriesControl?.setValue('other');
    }

    if (make && String(make).toUpperCase() !== 'OTHER') {
      this.vehicleFacade.loadModels(this.year, make);
    }

    this.modelControl?.enable();
    this.parentForm.updateValueAndValidity();
  }

  loadSeries() {
    this.resetVehicleDetails();
    this.resetMsrp();
    const { make, model } = this.parentForm.getRawValue();
    this.series = [];
    this.seriesControl?.reset();

    if (model && model === 'other') {
      this.seriesControl?.setValue('other');
    }

    if (
      make &&
      model &&
      String(make).toUpperCase() !== 'OTHER' &&
      String(model).toUpperCase() !== 'OTHER'
    ) {
      this.vehicleFacade.loadSeries(this.year, make, model);
      this.vehicleFacade.isLoading$
        .pipe(
          takeUntil(this.stop$),
          filter(loading => !loading),
          first(),
          tap(() => {
            this.cdr.detectChanges();
            this.resetMsrp();
            if (!this.series) {
              this.parentForm.removeControl('series');
              this.loadMsrp();
            }
          })
        )
        .subscribe();
      this.parentForm.addControl('series', new FormControl('', Validators.required));
      this.parentForm.updateValueAndValidity();
    }
  }

  onSeriesSelected() {
    this.resetVehicleDetails();
    this.resetMsrp();
    /*
    if (this.seriesControl?.value === 'other') {
      this.vehicleDetails = { newVehicle: null, features: null };
      this.vehicleDetailsLoaded.emit(this.vehicleDetails);
      this.parentForm.removeControl('msrp');
      this.parentForm.updateValueAndValidity();
      return;
    }*/
    this.loadMsrp();
  }

  loadMsrp() {
    const vehicle = this.parentForm.getRawValue();
    if ([vehicle.make, vehicle.model].includes('other')) return;

    this.vehicleService
      .getVehicleByAll(this.year, vehicle.make, vehicle.model, vehicle.series)
      .pipe(
        tap(vehicleInfo => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const automobiles = (vehicleInfo as any)?.matchedVehicles?.automobiles;
          if (automobiles && automobiles.length > 0) {
            const msrp = Number(automobiles[0].manufacturersSuggestedRetailPrice || 0);
            if (msrp > 0) {
              this.showMsrp = true;
              this.msrp = String(msrp);
              if (!this.parentForm.contains('msrp')) {
                this.parentForm.addControl(
                  'msrp',
                  new FormControl(
                    this.formatMSRP(String(this.msrp)),
                    Validators.compose([
                      Validators.required,
                      ValidationService.validMoneyValidator(),
                      ValidationService.minNumberValidator(+this.msrp),
                      ValidationService.maxNumberValidator(250000)
                    ])
                  )
                );
              } else this.parentForm.controls['msrp'].setValue(this.msrp);
            } else {
              this.showMsrp = false;
              this.parentForm.removeControl('mrsp');
            }
            this.parentForm.updateValueAndValidity();
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const features = this.vehicleFeaturesAdapterService.adapt(vehicleInfo as any).features;
          this.vehicleDetails = {
            newVehicle: {
              year: vehicle.year,
              vin: vehicle.vin,
              make: vehicle.make,
              model: vehicle.model,
              series: vehicle.series,
              msrp: this.msrp,
              estimatedValue: String(this.stripFormatting(String(this.msrpControl?.value || 0)))
            },
            features: features
          };
          this.vehicleDetailsLoaded.emit(this.vehicleDetails);
          if (this.parentForm.contains('msrp'))
            this.parentForm.controls['msrp'].valueChanges
              .pipe(
                takeUntil(this.stop$),
                tap(value => {
                  if (this.parentForm.controls['msrp'].valid) {
                    this.vehicleDetailsLoaded.emit({
                      ...this.vehicleDetails,
                      newVehicle: {
                        ...this.vehicleDetails.newVehicle,
                        estimatedValue: String(this.stripFormatting(value))
                      }
                    });
                  }
                })
              )
              .subscribe();
        }),
        catchError(() => {
          this.showMsrp = false;
          return of(null);
        }),
        finalize(() => this.cdr.detectChanges())
      )
      .subscribe();
  }

  getFormControlErrors(controlName: string): string[] {
    const control = this.parentForm.get(controlName);
    if (control && control.errors) {
      return Object.keys(control.errors).map(errorKey => this.getErrorMessage(errorKey));
    }
    return [];
  }

  getErrorMessage(errorKey: string): string {
    const errorMessages: { [key: string]: string } = {
      required: 'This field is required.',
      maxValue: `The estimated vehicle price can’t be more than $250,000.`,
      pattern: 'Invalid number',
      minValue: `Your vehicle price can’t be lower than the MSRP.`,
      invalidMoney: 'Invalid money'
    };
    return errorMessages[errorKey] || 'Invalid field.';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInput(e: any) {
    const value = this.stripFormatting(e.target.value);
    if (+value === 0) {
      e.target.value = '';
      return;
    }
    const formattedAmount = this.formatMSRP(String(value));
    e.target.value = formattedAmount;
  }

  clearAll() {
    this.models = this.series = [];
    this.makeControl?.setValue('');
    this.modelControl?.reset();
    this.seriesControl?.reset();
    this.modelControl?.disable();
    this.parentForm.updateValueAndValidity();
    this.resetMsrp();
  }

  resetAll() {
    this.models = this.series = [];
    this.parentForm.removeControl('make');
    this.parentForm.removeControl('model');
    this.parentForm.removeControl('series');
    this.parentForm.updateValueAndValidity();
    this.resetMsrp();
  }

  private resetMsrp() {
    this.showMsrp = false;
    this.msrp = undefined;
    this.parentForm.removeControl('msrp');
  }

  private resetVehicleDetails() {
    this.vehicleDetails = null;
    this.vehicleDetailsLoaded.emit(null);
  }

  private formatMSRP(msrp: string) {
    const value = msrp.replace(/[^0-9.-]+/g, '');
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.0-2');
  }

  private stripFormatting(msrp: string) {
    return Number(msrp.replace(/[^0-9.-]+/g, ''));
  }
}
